import React, { useState, useEffect } from 'react';
import { Plus, Loader2, AlertTriangle, Settings } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { ServiceOrderForm } from './ServiceOrderForm';
import { ServiceOrderSummaryCard } from './ServiceOrderSummaryCard';
import socketService from '../../services/socket';
import {
  ServiceOrderWithTasks,
  CreateServiceOrderRequest,
  UpdateServiceOrderRequest
} from '../../types/serviceOrder';
import { Task } from '../../types/task';
import {
  getServiceOrders,
  createServiceOrder,
  updateServiceOrder,
  deleteServiceOrder
} from '../../services/api';
import { usePermissions } from '../../contexts/PermissionsContext';

interface ServiceOrdersTabProps {
  projectId: string;
  hoursTrackingEnabled?: boolean;
}

export const ServiceOrdersTab: React.FC<ServiceOrdersTabProps> = ({ projectId, hoursTrackingEnabled }) => {
  const { canManage } = usePermissions();
  const canManageServiceOrders = canManage('serviceOrders');

  const [serviceOrders, setServiceOrders] = useState<ServiceOrderWithTasks[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form states
  const [showServiceOrderForm, setShowServiceOrderForm] = useState(false);
  const [editingServiceOrder, setEditingServiceOrder] = useState<ServiceOrderWithTasks | null>(null);
  const [formLoading, setFormLoading] = useState(false);


  // Load service orders
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getServiceOrders(projectId);
      const { serviceOrders: soList } = response.data.data;
      
      setServiceOrders(soList);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar ordens de serviço');
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  // Initialize Socket.io and load data
  useEffect(() => {
    loadData();
    
    // Connect to socket and join project room
    socketService.connect();
    socketService.joinProject(projectId);

    // Listen for real-time updates
    socketService.on('service-order:created', handleServiceOrderCreated);
    socketService.on('service-order:updated', handleServiceOrderUpdated);
    socketService.on('service-order:deleted', handleServiceOrderDeleted);

    // Listen for task assignment updates (affects hours in service order cards)
    socketService.on('task:assigned', handleTaskAssigned);
    socketService.on('task:unassigned', handleTaskUnassigned);
    socketService.on('hours:updated', handleHoursUpdated);

    // Listen for task JIRA updates (affects tasks within service orders)
    socketService.on('task-jira-updated', handleTaskJiraUpdate);
    socketService.on('task-updated', handleTaskUpdate);

    return () => {
      socketService.off('service-order:created', handleServiceOrderCreated);
      socketService.off('service-order:updated', handleServiceOrderUpdated);
      socketService.off('service-order:deleted', handleServiceOrderDeleted);
      socketService.off('task:assigned', handleTaskAssigned);
      socketService.off('task:unassigned', handleTaskUnassigned);
      socketService.off('hours:updated', handleHoursUpdated);
      socketService.off('task-jira-updated', handleTaskJiraUpdate);
      socketService.off('task-updated', handleTaskUpdate);
      socketService.leaveProject(projectId);
    };
  }, [projectId]);

  // Socket event handlers
  const handleServiceOrderCreated = (data: any) => {
    if (data.projectId === projectId) {
      loadData(); // Reload to get complete data
    }
  };

  const handleServiceOrderUpdated = (data: any) => {
    if (data.projectId === projectId) {
      loadData();
    }
  };

  const handleServiceOrderDeleted = (data: any) => {
    if (data.projectId === projectId) {
      loadData();
    }
  };

  // Task assignment socket event handlers (updates hours in cards)
  const handleTaskAssigned = (data: any) => {
    if (data.projectId === projectId) {
      loadData(); // Reload to get updated hours
    }
  };

  const handleTaskUnassigned = (data: any) => {
    if (data.projectId === projectId) {
      loadData(); // Reload to get updated hours
    }
  };

  const handleHoursUpdated = (data: any) => {
    if (data.projectId === projectId) {
      // Update the specific service order's hours without full reload
      setServiceOrders(currentOrders =>
        currentOrders.map(order => {
          if (order._id === data.serviceOrderId) {
            // Rebuild tasks array from tasksByArea
            const tasksFromAreas: Task[] = [];
            Object.values(data.tasksByArea || {}).forEach((areaData: any) => {
              if (areaData?.tasks) {
                tasksFromAreas.push(...areaData.tasks);
              }
            });

            return {
              ...order,
              tasksByArea: data.tasksByArea,
              totalHoras: data.totalHoras,
              tasks: tasksFromAreas,
              totalTasks: tasksFromAreas.length
            };
          }
          return order;
        })
      );
    }
  };

  // Task socket event handlers for JIRA updates
  const handleTaskJiraUpdate = (updatedTask: Task) => {
    // Atualizar tarefas dentro das service orders quando JIRA for atualizado
    setServiceOrders(currentOrders => 
      currentOrders.map(order => ({
        ...order,
        tasks: order.tasks.map(task => 
          task._id === updatedTask._id ? { ...task, ...updatedTask } : task
        )
      }))
    );
  };

  const handleTaskUpdate = (updatedTask: Task) => {
    // Atualizar tarefas dentro das service orders
    setServiceOrders(currentOrders => 
      currentOrders.map(order => ({
        ...order,
        tasks: order.tasks.map(task => 
          task._id === updatedTask._id ? updatedTask : task
        )
      }))
    );
  };

  // Service Order operations
  const handleCreateServiceOrder = async (data: CreateServiceOrderRequest) => {
    try {
      setFormLoading(true);
      await createServiceOrder(projectId, data);
      await loadData();
      setShowServiceOrderForm(false);
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Erro ao criar ordem de serviço');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateServiceOrder = async (data: UpdateServiceOrderRequest) => {
    if (!editingServiceOrder) return;

    try {
      setFormLoading(true);
      await updateServiceOrder(projectId, editingServiceOrder._id, data);
      await loadData();
      setEditingServiceOrder(null);
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Erro ao atualizar ordem de serviço');
    } finally {
      setFormLoading(false);
    }
  };

  // Unified handler for ServiceOrderForm
  const handleServiceOrderSubmit = async (data: CreateServiceOrderRequest | UpdateServiceOrderRequest) => {
    if (editingServiceOrder) {
      // Cast to UpdateServiceOrderRequest for editing
      await handleUpdateServiceOrder(data as UpdateServiceOrderRequest);
    } else {
      // Cast to CreateServiceOrderRequest for creation
      await handleCreateServiceOrder(data as CreateServiceOrderRequest);
    }
  };

  const handleDeleteServiceOrder = async (serviceOrderId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta ordem de serviço? As tarefas serão movidas para não atribuídas.')) {
      return;
    }

    try {
      await deleteServiceOrder(projectId, serviceOrderId);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir ordem de serviço');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner message="Carregando ordens de serviço" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {error && (
        <Card variant="glass" className="border-red-500/30 bg-red-500/10">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-300"
            >
              ✕
            </Button>
          </CardContent>
        </Card>
      )}


      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-heading text-foreground-primary">
          Ordens de Serviço
        </h2>
        {canManageServiceOrders && (
          <Button
            variant="gradient"
            size="sm"
            onClick={() => setShowServiceOrderForm(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova Ordem de Serviço
          </Button>
        )}
      </div>

      {/* Service Orders Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {serviceOrders.length > 0 ? (
          serviceOrders.map((serviceOrder) => (
            <ServiceOrderSummaryCard
              key={serviceOrder._id}
              serviceOrder={serviceOrder}
              projectId={projectId}
              onEdit={canManageServiceOrders ? setEditingServiceOrder : undefined}
              onDelete={canManageServiceOrders ? handleDeleteServiceOrder : undefined}
              hoursTrackingEnabled={hoursTrackingEnabled}
            />
          ))
        ) : (
          <div className="col-span-full">
            <Card variant="glass">
              <CardContent className="p-8 text-center">
                <Settings className="w-12 h-12 text-foreground-tertiary mx-auto mb-4" />
                <h3 className="text-lg font-heading text-foreground-primary mb-2">
                  Nenhuma ordem de serviço criada
                </h3>
                <p className="text-foreground-tertiary mb-4">
                  {canManageServiceOrders
                    ? 'Crie sua primeira ordem de serviço para organizar as tarefas do projeto'
                    : 'Nenhuma ordem de serviço disponível no momento'}
                </p>
                {canManageServiceOrders && (
                  <Button
                    variant="gradient"
                    onClick={() => setShowServiceOrderForm(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Criar Primeira Ordem de Serviço
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Forms */}
      {(showServiceOrderForm || editingServiceOrder) && (
        <ServiceOrderForm
          serviceOrder={editingServiceOrder}
          onSubmit={handleServiceOrderSubmit}
          onCancel={() => {
            setShowServiceOrderForm(false);
            setEditingServiceOrder(null);
          }}
          loading={formLoading}
        />
      )}
    </div>
  );
};